import type { RefObject } from 'react'
import { TrayGrid, TrayLoadingSkeleton } from '@components/app/panels/right-panel/tray'
import { ClikArtThumbnail } from '@/components/app/clikart/clikart-thumbnail'
import type { ClikArtItem } from '@/api/types/clikart'
import { clikArtGalleryColumnCount } from '@/lib/clikart/clikart-gallery-columns'

/** Enough placeholder cards to fill the panel's first screen (WMPCM-12011). */
const CLIKART_LOADING_SKELETON_COUNT = 12

type ClikArtGalleryProps = {
  isLoading: boolean
  isError: boolean
  resultCount: number
  visibleResults: ClikArtItem[]
  hasMore: boolean
  sentinelRef: RefObject<HTMLDivElement | null>
  showFilename: boolean
  onPreviewOpenChange?: (open: boolean) => void
}

/**
 * The ClikArt results: loading / error / empty message, or the lazily-grown results grid. It has
 * no scroll box of its own (WMPCM-11930) — it renders inside the panel's single scroll container
 * (`TrayPanelLayout`), which is also the `useTrayLazyWindow` scroll root the sentinel is observed
 * against.
 */
export function ClikArtGallery({
  isLoading,
  isError,
  resultCount,
  visibleResults,
  hasMore,
  sentinelRef,
  showFilename,
  onPreviewOpenChange,
}: ClikArtGalleryProps) {
  const columns = clikArtGalleryColumnCount(resultCount)

  if (isLoading) {
    // Two columns, as in the design: the real column count isn't known until results arrive.
    return (
      <TrayLoadingSkeleton
        label="Loading ClikArt"
        columns={2}
        count={CLIKART_LOADING_SKELETON_COUNT}
        framed
        // `TrayThumbnailCard`'s shell, so the real cards swap in without a layout shift.
        frameClassName="rounded-md bg-background"
        itemClassName="motion-reduce:animate-none"
      />
    )
  }

  if (isError) {
    return <p className="px-1 py-4 text-sm text-destructive">Unable to load ClikArt.</p>
  }

  if (resultCount === 0) {
    return <p className="px-1 py-4 text-sm text-muted-foreground">No ClikArt found.</p>
  }

  // WMPCM-11008 (US-12) — `ClikArtThumbnail`/`TrayThumbnailCard` (Photos' same shared card)
  // supplies hover/keyboard preview and the "Placed" badge; `ClikArtThumbnail` also carries the
  // WMPCM-11014 drag-to-place wiring, so it stays the thumbnail used here rather than the plainer
  // `ClikArtTrayThumbnail`. `TrayGrid` is its matching container (the card sizes itself to a
  // `1fr` grid track, not a fixed pixel width). WMPCM-11002 (US-20): one column below 25 matching
  // results, two from 25 up. The count is the whole filtered list, not the lazily-windowed slice,
  // so it does not change while scrolling. Single-column cards disable the hover/focus preview.
  return (
    <TrayGrid columns={columns} sentinelRef={sentinelRef} showSentinel={hasMore}>
      {visibleResults.map((item) => (
        <ClikArtThumbnail
          key={item.artId}
          item={item}
          columns={columns}
          previewEnabled={columns === 2}
          showFilename={showFilename}
          onPreviewOpenChange={onPreviewOpenChange}
        />
      ))}
    </TrayGrid>
  )
}
